import type { AlertListItem } from '@minisoc/contracts';
import {
  AlertStatusBadge,
  DataTable,
  FilterTabs,
  Pagination,
  PageHeader,
  SeverityDot,
  StatCard,
  formatTime,
  type Column,
} from '@minisoc/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';

/**
 * Filtres rapides de la maquette. Chaque entrée décrit son propre jeu de
 * paramètres API : l'onglet et la requête ne peuvent pas désynchroniser.
 */
const FILTERS = [
  { value: 'all', label: 'Toutes', params: {} },
  { value: 'critical', label: 'Critiques', params: { severity: ['CRITICAL'] } },
  { value: 'high', label: 'Élevées', params: { severity: ['HIGH'] } },
  { value: 'open', label: 'Ouvertes', params: { status: ['OPEN', 'IN_PROGRESS'] } },
] as const;

type FilterValue = (typeof FILTERS)[number]['value'];

export function AlertsPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterValue>('all');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const params = FILTERS.find((entry) => entry.value === filter)?.params ?? {};

  const alerts = useQuery({
    queryKey: ['alerts', 'list', filter, page, search],
    queryFn: () => api.listAlerts({ ...params, page, pageSize: 25, search: search || undefined }),
    // Les alertes arrivent aussi par WebSocket ; ce délai borne la dérive si la
    // connexion temps réel est tombée sans que le client s'en aperçoive.
    refetchInterval: 60_000,
  });

  // Les compteurs sont volontairement dérivés d'un appel KPI dédié et non de la
  // page courante : ils doivent refléter l'ensemble du parc du client, pas les
  // 25 lignes affichées.
  const kpi = useQuery({ queryKey: ['kpi', 'counters'], queryFn: () => api.getKpiDashboard(30) });

  const columns: Column<AlertListItem>[] = [
    {
      id: 'rule',
      header: 'Règle',
      cell: (alert) => <span className="font-mono text-brand-400">{alert.ruleCode}</span>,
      className: 'w-32',
    },
    {
      id: 'name',
      header: 'Alerte',
      cell: (alert) => (
        <span className="flex items-center gap-2.5">
          <SeverityDot severity={alert.severity} />
          <span className="font-medium">{alert.ruleNameFr}</span>
        </span>
      ),
    },
    {
      id: 'endpoint',
      header: 'Machine',
      cell: (alert) => <span className="font-mono text-sm">{alert.endpointName}</span>,
    },
    {
      id: 'user',
      header: 'Utilisateur',
      cell: (alert) => alert.subjectUser ?? <span className="text-text-dim">—</span>,
    },
    {
      id: 'time',
      header: 'Heure',
      cell: (alert) => <span className="font-mono text-sm">{formatTime(alert.detectedAt)}</span>,
      className: 'w-24',
    },
    {
      id: 'status',
      header: 'Statut',
      cell: (alert) => <AlertStatusBadge status={alert.status} resolution={alert.resolution} />,
      className: 'w-32',
    },
  ];

  return (
    <>
      <PageHeader title="Alertes" subtitle="Surveillance en temps réel de votre infrastructure" />

      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Critiques" value={kpi.data?.counters.critical ?? '—'} tone="text-critical" />
        <StatCard label="Élevées" value={kpi.data?.counters.high ?? '—'} tone="text-high" />
        <StatCard
          label="Résolues (30j)"
          value={kpi.data?.counters.resolvedInRange ?? '—'}
          tone="text-success"
        />
        <StatCard
          label="Machines"
          value={kpi.data?.counters.monitoredEndpoints ?? '—'}
          tone="text-brand-400"
        />
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <FilterTabs
          options={FILTERS.map(({ value, label }) => ({ value, label }))}
          value={filter}
          onChange={(value) => {
            setFilter(value);
            setPage(1);
          }}
        />

        <input
          type="search"
          placeholder="Rechercher une machine, un compte, une règle…"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          className="w-72 rounded-lg border border-border-subtle bg-surface-sunken px-3 py-2 text-sm text-text outline-none placeholder:text-text-dim focus:border-brand-500"
        />
      </div>

      <DataTable
        columns={columns}
        rows={alerts.data?.data ?? []}
        rowKey={(alert) => alert.id}
        onRowClick={(alert) => navigate(`/alertes/${alert.id}`)}
        loading={alerts.isPending}
        emptyTitle="Aucune alerte"
        emptyDescription={
          search || filter !== 'all'
            ? 'Aucune alerte ne correspond à ces critères.'
            : 'Votre infrastructure ne présente aucune alerte. Les nœuds continuent de surveiller en continu.'
        }
      />

      {alerts.data ? (
        <Pagination
          page={alerts.data.meta.page}
          pageCount={alerts.data.meta.pageCount}
          total={alerts.data.meta.total}
          onPageChange={setPage}
        />
      ) : null}
    </>
  );
}
